// Libs
import jwtDecode from 'jwt-decode';
// App
import { DecodedAccessToken, Session } from './auth.interfaces';

const MILLISECONDS_PER_SECOND = 1000;

/**
 * Decode an access token, without throwing on malformed tokens
 * @param accessToken the raw JWT issued by the api
 * @returns the decoded token, or null if it cannot be decoded
 */
export const decodeAccessToken = (accessToken: string): DecodedAccessToken => {
  try {
    return jwtDecode<DecodedAccessToken>(accessToken);
  } catch {
    return null;
  }
};

/**
 * Check if an access token is expired.
 * The next-auth session lives much longer than the api token, so an expired token
 * must invalidate the session instead of letting pages render with a dead token.
 * @param accessToken the raw JWT issued by the api
 * @returns true if the token is missing, malformed or expired, false else
 */
export const isAccessTokenExpired = (accessToken: string): boolean => {
  if (!accessToken) return true;

  const decodedAccessToken = decodeAccessToken(accessToken);

  if (!decodedAccessToken || !decodedAccessToken.exp) return true;

  const expirationTimestamp = decodedAccessToken.exp * MILLISECONDS_PER_SECOND;
  const isExpired = expirationTimestamp <= Date.now();

  return isExpired;
};

/**
 * Check if a session holds a usable access token
 * @param session the session of user
 * @returns true if the session can be used to call the api, false else
 */
export const hasValidAccessToken = (session: Session): boolean => {
  if (!session || !session.accessToken) return false;

  const isValid = !isAccessTokenExpired(session.accessToken);

  return isValid;
};
